import * as ss from 'superstruct';
import { ConversionEventData } from './conversion-data';
import { ConversionConsent } from './conversion-consent';
import { Id } from '../types';
import { ConversionSource } from './conversion-source';

/**
 * This data structure is relied on externally by inbound and
 * output webhooks and possibly other integrations. Keep changes
 * additive.
 *
 * THIS TYPE IS RELIED ON EXTERNALLY.
 */
export const ConversionResourceStringDataFields = {
  firstName: ss.nullable(ss.optional(ss.string())),
  lastName: ss.nullable(ss.optional(ss.string())),
  email: ss.nullable(ss.optional(ss.string())),
  phone: ss.nullable(ss.optional(ss.string())),
  phoneNormalized: ss.nullable(ss.optional(ss.string())),
  gender: ss.nullable(ss.optional(ss.string())),
  dob: ss.nullable(ss.optional(ss.string())),
  streetAddress: ss.nullable(ss.optional(ss.string())),
  city: ss.nullable(ss.optional(ss.string())),
  state: ss.nullable(ss.optional(ss.string())),
  zip: ss.nullable(ss.optional(ss.string())),
  country: ss.nullable(ss.optional(ss.string())),
  userAgent: ss.nullable(ss.optional(ss.string())),
  clientAddress: ss.nullable(ss.optional(ss.string())),
  url: ss.nullable(ss.optional(ss.string())),
  destinationPhone: ss.nullable(ss.optional(ss.string())),

  // Explicit Attribution Fields
  accountId: ss.nullable(ss.optional(ss.string())),
  campaignId: ss.nullable(ss.optional(ss.string())),
  adGroupId: ss.nullable(ss.optional(ss.string())),
  adId: ss.nullable(ss.optional(ss.string())),
};

export const ConversionResourceNumberDataFields = {
  value: ss.nullable(ss.optional(ss.number())),
};

/**
 * Fields that were removed from the resource but that producers may still
 * send for a while.  Inbound paths strip these instead of rejecting the
 * conversion or stashing them in `otherData`.
 */
export const ConversionResourceIgnoredDataFields: readonly string[] = [
  'latitude',
  'longitude',
];

export const ConversionResource = ss.object({
  /** DB Assigned ID. */
  id: Id,
  /** Where the conversion originated. */
  source: ConversionSource,
  /** What time the conversion happened, in Epoch MS. */
  eventTimeMs: ss.number(),
  /** Specific type of event, for this source. */
  eventType: ss.string(),
  /**
   * And optional key which identifies uniqueness w/in this `source`
   * and `eventType` partition.
   */
  eventKey: ss.nullable(ss.optional(ss.string())),
  /** Optional user ID, which identifies a user w/in a given `source`
   *
   * This is important in attribution because it allows us to link
   * multiple conversions (within a source) to a single user, and
   * extract linkable data like emails, phone numbers, and tracked calls.
   */
  user: ss.nullable(ss.optional(ss.string())),
  /** Misc. data that isn't standard. */
  otherData: ss.nullable(ss.optional(ConversionEventData)),
  /** System set - when this conversion was created. */
  createdAt: ss.nullable(ss.optional(ss.date())),
  /** System set - when this conversion was last updated. */
  updatedAt: ss.nullable(ss.optional(ss.date())),
  /**
   * If this is attributable. Some "conversions" are not
   * business marketing, like employment applications.
   */
  attribute: ss.boolean(),
  /**
   * Friendly name for the conversion type.  Shown as "Conversion Type"
   * in emails, for example.
   */
  friendlyName: ss.nullable(ss.optional(ss.string())),
  /**
   * Optional field order for the conversion.  This is used to order fields
   * in emails, for example.  Fields not in this list will be appended after
   * in an undefined order.
   */
  fieldOrder: ss.nullable(ss.optional(ss.array(ss.string()))),
  /**
   * Consent status observed when the conversion was captured.  Validated
   * against the `AuConsentStatus` shape from `@arrowsup/websites-common`.
   */
  consent: ss.nullable(ss.optional(ConversionConsent)),
  ...ConversionResourceStringDataFields,
  ...ConversionResourceNumberDataFields,
});

export type ConversionResource = ss.Infer<typeof ConversionResource>;
